import React, { useEffect, useMemo, useState } from "react";
import Card from "./Card";                   // adjust import if needed
import InputField from "../elements/InputField";
import DropdownField from "../elements/DropdownField";
import Text from "../elements/TextComponent";

export type BotSummary = { botId: string; name: string };

type PositionKind = "bottom-right" | "bottom-left" | "custom";
type ThemeKind = "purple" | "blue" | "green" | "custom";
type ModeKind = "inline" | "overlay"; // overlay = with launcher (floating), inline = in-flow
type BoolChoice = "true" | "false";

type Props = {
  bots: BotSummary[];

};

const isHex = (s: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(s || "").trim());
const isLen = (s: string) => /^\d+(px|em|rem|%)$/i.test(String(s || "").trim());
const toHex = (s: string) => (s?.trim().startsWith("#") ? s.trim() : `#${s?.trim()}`);

const V2BotEmbedBuilder: React.FC<Props> = ({
  bots,
}) => {
  // ---- Config base ----
  const computedBase = process.env.REACT_APP_API_URL!;

  // ---- Selection ----
  const [selectedBotId, setSelectedBotId] = useState<string>(() => bots[0]?.botId || "");
  const selectedBot = useMemo(
    () => bots.find((b) => b.botId === selectedBotId) || bots[0],
    [bots, selectedBotId]
  );

  // ---- Core fields ----
  const [displayName, setDisplayName] = useState<string>(selectedBot?.name || "");
  const [welcome, setWelcome] = useState<string>("Hi!");
  const [mode, setMode] = useState<ModeKind>("overlay");         // v2
  const [variant, setVariant] = useState<string>("default");    // v2 (free string)

  // ---- Theme / Style ----
  const [themeKind, setThemeKind] = useState<ThemeKind>("purple");
  const [customHex, setCustomHex] = useState<string>("#8b2cd7");
  const [radius, setRadius] = useState<string>("16px");         // CSS length
  const [bg, setBg] = useState<string>("");                     // optional hex (card bg)

  const [font, setFont] = useState<string>("Inter, system-ui, sans-serif");
  const [fontUrl, setFontUrl] = useState<string>("https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap");

  // ---- Behavior ----
  const [launcher, setLauncher] = useState<"true" | "none">("true"); // v2
  const [buttonLabel, setButtonLabel] = useState<string>("Chat");
  const [allowSession, setAllowSession] = useState<BoolChoice>("true");
  const [speed, setSpeed] = useState<number>(25); // 10..60

  // ---- Dimension (query + style) ----
  const [width, setWidth] = useState<number>(320);
  const [height, setHeight] = useState<number>(540);

  // ---- Fixed position (style only) ----
  const [position, setPosition] = useState<PositionKind>("bottom-right");
  const [padding, setPadding] = useState<number>(16);
  const [top, setTop] = useState<string>("");
  const [right, setRight] = useState<string>("");
  const [bottom, setBottom] = useState<string>("");
  const [left, setLeft] = useState<string>("");

  // ---- Suggestions (repeatable) ----
  const [suggestions, setSuggestions] = useState<string[]>([
    "Invent me a funny pun about coffee and Mondays",
    "Tell me a clean dad joke about penguins",
    "Write a knock-knock joke about time travel",
    "Give me a witty one-liner roasting meetings",
    "Tell a tech pun about JavaScript and bugs",
  ]);
  const [newSuggest, setNewSuggest] = useState("");

  // ==== CONVERSATION BUILDER (NEW) ====
  type ConvResp = { label: string; answer: string };
  type ConvStep = { question: string; responses: ConvResp[] };

  const [conversationSteps, setConversationSteps] = useState<ConvStep[]>([]);
  const [newQuestion, setNewQuestion] = useState<string>("");

  const addQuestion = () => {
    const q = newQuestion.trim();
    if (!q) return;
    setConversationSteps(prev => [...prev, { question: q, responses: [] }]);
    setNewQuestion("");
  };

  const updateQuestion = (idx: number, value: string) => {
    setConversationSteps(prev => prev.map((s, i) => i === idx ? { ...s, question: value } : s));
  };

  const removeQuestion = (idx: number) => {
    setConversationSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const addResponse = (qIdx: number) => {
    setConversationSteps(prev => prev.map((s, i) => i === qIdx ? { ...s, responses: [...s.responses, { label: "", answer: "" }] } : s));
  };

  const updateResponse = (qIdx: number, rIdx: number, key: "label" | "answer", value: string) => {
    setConversationSteps(prev => prev.map((s, i) => {
      if (i !== qIdx) return s;
      const rs = s.responses.map((r, j) => j === rIdx ? { ...r, [key]: value } : r);
      return { ...s, responses: rs };
    }));
  };

  const removeResponse = (qIdx: number, rIdx: number) => {
    setConversationSteps(prev => prev.map((s, i) => {
      if (i !== qIdx) return s;
      const rs = s.responses.filter((_, j) => j !== rIdx);
      return { ...s, responses: rs };
    }));
  };
  // ==== /CONVERSATION BUILDER (NEW) ====

  // ---- Output ----
  const [snippet, setSnippet] = useState<string>("");

  useEffect(() => {
    setDisplayName(selectedBot?.name || "");
  }, [selectedBot?.name]);

  const themeParam = useMemo(() => {
    if (themeKind !== "custom") return themeKind;
    const hex = toHex(customHex);
    return isHex(hex) ? hex : "#8b2cd7";
  }, [themeKind, customHex]);

  const bgParam = useMemo(() => {
    if (!bg.trim()) return "";
    const hex = toHex(bg);
    return isHex(hex) ? hex : "";
  }, [bg]);

  const radiusParam = useMemo(() => {
    const v = String(radius || "").trim();
    return isLen(v) ? v : "16px";
  }, [radius]);

  const buildSrc = (): string => {
    const u = new URL("/chat/embed/v2", computedBase);

    const params: Record<string, string> = {
      botId: selectedBot?.botId || "",
      name: displayName || selectedBot?.name || "Assistant",
      mode,                     // 'inline' or 'overlay'
      variant,                  // e.g. 'default'
      theme: themeParam,        // preset or hex
      radius: radiusParam,      // CSS length
      welcome: welcome || "",
      launcher,                 // 'true' or 'none'
      buttonLabel: buttonLabel || "Chat",
      allowSession,             // 'true' | 'false'
      speed: String(Math.min(60, Math.max(10, Number(speed) || 25))),
      font: font || "",
      fontUrl: fontUrl || "",
      h: String(height),
      w: String(width),
    };

    if (bgParam) params.bg = bgParam;

    // Apply params
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));

    // Multi-suggest
    suggestions
      .filter(s => s && s.trim())
      .forEach(s => u.searchParams.append("suggest", s.trim()));

    // ==== CONVERSATION BUILDER (NEW): append repeated conversation params ====
    // For each step: append ?conversation=<question> and then ?conversation=<label--answer::::...>
    conversationSteps.forEach(step => {
      const q = (step.question || "").trim();
      if (!q) return;

      const pairs = step.responses
        .map(r => ({ label: (r.label || "").trim(), answer: (r.answer || "").trim() }))
        .filter(r => r.label.length > 0) // allow empty answer (=> "label--")
        .map(r => `${r.label}--${r.answer}`);

      // Always append the question
      u.searchParams.append("conversation", q);

      // Append choices only if there is at least one label
      if (pairs.length > 0) {
        u.searchParams.append("conversation", pairs.join("::::"));
      }
    });
    // ==== /CONVERSATION BUILDER (NEW) ====

    return u.toString();
  };

  const buildStyle = (): string => {
    // If mode=inline, people often place the iframe inside the layout (non-fixed). But
    // the builder still outputs a fixed snippet for convenience; they can remove position if desired.
    const base = [
      `position:${mode === "inline" ? "relative" : "fixed"}`,
      `width:${width}px`,
      `height:${height}px`,
      `border:0`,
      `z-index:2147483647`,
    ];

    if (mode !== "inline") {
      if (position === "bottom-right") {
        base.push(`bottom:${padding}px`, `right:${padding}px`);
      } else if (position === "bottom-left") {
        base.push(`bottom:${padding}px`, `left:${padding}px`);
      } else {
        const edges: string[] = [];
        if (top.trim()) edges.push(`top:${top.trim()}`);
        if (right.trim()) edges.push(`right:${right.trim()}`);
        if (bottom.trim()) edges.push(`bottom:${bottom.trim()}`);
        if (left.trim()) edges.push(`left:${left.trim()}`);
        if (edges.length === 0) edges.push(`bottom:${padding}px`, `right:${padding}px`);
        base.push(...edges);
      }
    }

    return base.join(";");
  };

  const generate = () => {
    if (!selectedBot?.botId) return;
    const src = buildSrc();
    const style = buildStyle();
    const html = `<iframe
  src="${src}"
  style="${style};"
  scrolling="no"
></iframe>`;
    setSnippet(html);
  };

  const copy = async () => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
  };

  const label: React.CSSProperties = { color: "var(--text)", fontSize: 13, fontWeight: 600 };

  const removeSuggest = (i: number) => {
    setSuggestions(prev => prev.filter((_, idx) => idx !== i));
  };
  const addSuggest = () => {
    const v = newSuggest.trim();
    if (!v) return;
    setSuggestions(prev => [...prev, v]);
    setNewSuggest("");
  };

  return (
    <Card
      width="92%"
      height="max-content"
      style={{
        padding: 16,
        fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        overflow: "hidden",
      }}
    >
      <h2 style={{ margin: "0 0 12px", fontWeight: 800, color: "var(--text)" }}>
        Generate Embed Iframe (v2)
      </h2>
      <Text
        text="Configure your v2 assistant embed. Choose mode (inline or overlay launcher), brand it, add suggestions, and copy the iframe."
        fontSize="14px"
        fontWeight="400"
        lineHeight="22px"
        color="#111827"
        textAlign="left"
      />
      <div style={{ height: 8 }} />

      {/* -------------------- Form (scrollable) -------------------- */}
      <div style={{ maxHeight: 540, overflowY: "auto", paddingRight: 4 }}>
        {/* Bot & Basic */}
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(2, minmax(0, 1fr))", alignItems: "start" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Bot</span>
            <DropdownField
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              options={bots.map((b) => ({ label: `${b.name} (${b.botId})`, value: b.botId }))}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Name (header)</span>
            <InputField value={displayName} onChange={(e) => setDisplayName(e.target.value)} text={selectedBot?.name || "Assistant"} />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Welcome</span>
            <InputField value={welcome} onChange={(e) => setWelcome(e.target.value)} text="Hi!" />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Mode</span>
            <DropdownField
              value={mode}
              onChange={(e) => setMode(e.target.value as ModeKind)}
              options={[
                { label: "overlay (floating launcher)", value: "overlay" },
                { label: "inline (in-flow)", value: "inline" },
              ]}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Variant</span>
            <InputField value={variant} onChange={(e) => setVariant(e.target.value)} text="default" />
          </label>

          {/* Theme preset / custom */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Theme</span>
            <DropdownField
              value={themeKind}
              onChange={(e) => setThemeKind(e.target.value as ThemeKind)}
              options={[
                { label: "purple", value: "purple" },
                { label: "blue", value: "blue" },
                { label: "green", value: "green" },
                { label: "custom (hex)", value: "custom" },
              ]}
            />
          </label>

          {themeKind === "custom" && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={label}>Custom Hex</span>
              <InputField value={customHex} onChange={(e) => setCustomHex(e.target.value)} text="#8b2cd7" />
            </label>
          )}

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Radius (px/em/rem/%)</span>
            <InputField value={radius} onChange={(e) => setRadius(e.target.value)} text="16px" />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Card Background (hex, optional)</span>
            <InputField value={bg} onChange={(e) => setBg(e.target.value)} text="#ffffff" />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Font family</span>
            <InputField value={font} onChange={(e) => setFont(e.target.value)} text="Inter, system-ui, sans-serif" />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Font URL</span>
            <InputField value={fontUrl} onChange={(e) => setFontUrl(e.target.value)} text="https://fonts.googleapis.com/..." />
          </label>

          {/* Behavior */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Launcher</span>
            <DropdownField
              value={launcher}
              onChange={(e) => setLauncher(e.target.value as "true" | "none")}
              options={[
                { label: "true (button toggles chat)", value: "true" },
                { label: "none (chat open by default)", value: "none" },
              ]}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Button Label</span>
            <InputField value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} text="Chat" />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Allow Session</span>
            <DropdownField
              value={allowSession}
              onChange={(e) => setAllowSession(e.target.value as BoolChoice)}
              options={[
                { label: "true", value: "true" },
                { label: "false", value: "false" },
              ]}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Type Speed (10–60)</span>
            <InputField
              value={String(speed)}
              onChange={(e) => setSpeed(parseInt(e.target.value || "25", 10))}
              text="25"
            />
          </label>

          {/* Size */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Width (w param & style px)</span>
            <InputField
              value={String(width)}
              onChange={(e) => setWidth(parseInt(e.target.value || "0", 10))}
              text="282"
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Height (h param & style px)</span>
            <InputField
              value={String(height)}
              onChange={(e) => setHeight(parseInt(e.target.value || "0", 10))}
              text="540"
            />
          </label>

          {/* Position (for overlay/fixed) */}
          {mode !== "inline" && (
            <>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={label}>Position</span>
                <DropdownField
                  value={position}
                  onChange={(e) => setPosition(e.target.value as PositionKind)}
                  options={[
                    { label: "bottom-right", value: "bottom-right" },
                    { label: "bottom-left", value: "bottom-left" },
                    { label: "custom", value: "custom" },
                  ]}
                />
              </label>

              {position !== "custom" ? (
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={label}>Padding (px)</span>
                  <InputField value={String(padding)} onChange={(e) => setPadding(parseInt(e.target.value || "16", 10))} text="16" />
                </label>
              ) : (
                <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={label}>top</span>
                    <InputField width="auto" value={top} onChange={(e) => setTop(e.target.value)} text="e.g. 16px" />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={label}>right</span>
                    <InputField width="auto" value={right} onChange={(e) => setRight(e.target.value)} text="e.g. 16px" />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={label}>bottom</span>
                    <InputField width="auto" value={bottom} onChange={(e) => setBottom(e.target.value)} text="e.g. 16px" />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={label}>left</span>
                    <InputField width="auto" value={left} onChange={(e) => setLeft(e.target.value)} text="e.g. 16px" />
                  </label>

                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={label}>Padding (px)</span>
                    <InputField value={String(padding)} onChange={(e) => setPadding(parseInt(e.target.value || "16", 10))} text="16" />
                  </label>
                </div>
              )}
            </>
          )}

          {/* Suggestions */}
          <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Suggestions (repeatable)</span>
            <div style={{ display: "flex", gap: 8 }}>
              <InputField
                width="100%"
                value={newSuggest}
                onChange={(e) => setNewSuggest(e.target.value)}
                text="Type a suggestion and click Add"
              />
              <button
                onClick={addSuggest}
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  border: 0,
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Add
              </button>
            </div>
            {!!suggestions.length && (
              <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                {suggestions.map((s, i) => (
                  <li key={`${s}-${i}`} style={{ marginBottom: 6 }}>
                    <code style={{ background: "#f3f4f6", padding: "2px 6px", borderRadius: 6 }}>{s}</code>{" "}
                    <button
                      onClick={() => removeSuggest(i)}
                      style={{
                        marginLeft: 6,
                        background: "transparent",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "2px 8px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ==== CONVERSATION BUILDER (NEW) ==== */}
          <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
            <span style={label}>Start Conversation (optional)</span>

            {/* Add new question */}
            <div style={{ display: "flex", gap: 8 }}>
              <InputField
                width="100%"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                text="Type a question/prompt and click Add"
              />
              <button
                onClick={addQuestion}
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  border: 0,
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Add
              </button>
            </div>

            {/* List of questions & responses */}
            {!!conversationSteps.length && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                {conversationSteps.map((step, qIdx) => (
                  <div key={`q-${qIdx}`} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ ...label, minWidth: 70 }}>Question</span>
                      <InputField
                        width="100%"
                        value={step.question}
                        onChange={(e) => updateQuestion(qIdx, e.target.value)}
                        text="Question..."
                      />
                      <button
                        onClick={() => removeQuestion(qIdx)}
                        style={{
                          background: "transparent",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "6px 10px",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        remove
                      </button>
                    </div>

                    {/* Responses */}
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ ...label, minWidth: 70 }}>Responses</span>
                        <button
                          onClick={() => addResponse(qIdx)}
                          style={{
                            background: "var(--primary)",
                            color: "#fff",
                            border: 0,
                            borderRadius: 8,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontWeight: 700,
                          }}
                        >
                          + Add response
                        </button>
                      </div>

                      {!!step.responses.length && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8 }}>
                          {step.responses.map((r, rIdx) => (
                            <React.Fragment key={`r-${qIdx}-${rIdx}`}>
                              <InputField
                                width="100%"
                                value={r.label}
                                onChange={(e) => updateResponse(qIdx, rIdx, "label", e.target.value)}
                                text="Label (e.g., yes)"
                              />
                              <InputField
                                width="100%"
                                value={r.answer}
                                onChange={(e) => updateResponse(qIdx, rIdx, "answer", e.target.value)}
                                text="Answer (may be empty)"
                              />
                              <button
                                onClick={() => removeResponse(qIdx, rIdx)}
                                style={{
                                  background: "transparent",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                }}
                              >
                                remove
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* ==== /CONVERSATION BUILDER (NEW) ==== */}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
        <button
          onClick={generate}
          style={{
            background: "var(--primary)",
            color: "#fff",
            border: 0,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          Generate
        </button>
        <button
          onClick={copy}
          disabled={!snippet}
          style={{
            background: snippet ? "var(--primary)" : "#9CA3AF",
            color: "#fff",
            border: 0,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 800,
            cursor: snippet ? "pointer" : "not-allowed",
            opacity: snippet ? 1 : 0.6,
            fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          Copy
        </button>
      </div>

      {/* Output */}
      <pre
        style={{
          margin: 0,
          background: "var(--tint, #f9f5ff)",
          color: "var(--text, #111827)",
          borderRadius: 12,
          padding: 12,
          border: "1px solid var(--hover, #7621b9)",
          outline: "2px solid rgba(118, 33, 185, 0.12)",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 13,
        }}
      >
        {snippet || "<!-- Your v2 embed snippet will appear here after you click Generate -->"}
      </pre>
    </Card>
  );
};

export default V2BotEmbedBuilder;
